import { useState, type FormEvent } from "react";
import { LangToggle } from "../components/LangToggle";
import { ThemeToggle } from "../components/ThemeToggle";
import { Button, Card, Input } from "../components/ui";
import { useT } from "../i18n";
import { authClient } from "../lib/auth";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 2FA: nach twoFactorRedirect fragt ein zweiter Schritt den E-Mail-Code ab;
  // "forgot" ist der Passwort-vergessen-Schritt (Reset-Link per Mail)
  const [step, setStep] = useState<"credentials" | "otp" | "forgot">("credentials");
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(true);
  const [info, setInfo] = useState<string | null>(null);
  const t = useT();

  async function sendCode() {
    setInfo(null);
    setError(null);
    const { error } = await authClient.twoFactor.sendOtp();
    if (error) setError(t.twofa.sendFailed);
    else setInfo(t.twofa.sent);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error } = await authClient.signIn.email({ email, password });
    if (error) {
      const code = (error as { code?: string }).code;
      setError(
        code === "BANNED_USER"
          ? t.login.errorBanned
          : error.status === 429
            ? t.login.errorTooMany
            : code === "INVALID_ORIGIN"
              ? t.login.errorOrigin
              : t.login.errorInvalid,
      );
      setBusy(false);
      return;
    }
    if ((data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect) {
      setBusy(false);
      setStep("otp");
      await sendCode();
      return;
    }
    // Erfolg: useSession() aktualisiert sich, App rendert das Dashboard.
  }

  async function submitForgot(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    const { error } = await authClient.requestPasswordReset({
      email,
      redirectTo: "/reset-password",
    });
    if (error) {
      // Anti-Enumeration übernimmt der Server (immer 200) — Fehler heißt hier:
      // Rate-Limit oder kein Mail-Versand konfiguriert
      setError(error.status === 429 ? t.login.errorTooMany : t.common.error);
    } else {
      setInfo(t.login.forgotSent);
    }
    setBusy(false);
  }

  async function submitOtp(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await authClient.twoFactor.verifyOtp({ code: code.trim(), trustDevice });
    if (error) {
      setError(error.status === 429 ? t.login.errorTooMany : t.twofa.wrongCode);
      setBusy(false);
    }
    // Erfolg: Session gesetzt, useSession() übernimmt.
  }

  return (
    <div className="relative grid min-h-dvh place-items-center px-4">
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <LangToggle />
        <ThemeToggle />
      </div>
      <main className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold tracking-tight">
            fast<span className="text-accent">360</span>
          </h1>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{t.login.tagline}</p>
        </div>
        <Card>
          {step === "credentials" ? (
            <form onSubmit={(e) => void submit(e)} className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  {t.login.email}
                </span>
                <Input
                  type="email"
                  name="email"
                  className="w-full"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                  autoFocus
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  {t.login.password}
                </span>
                <Input
                  type="password"
                  name="password"
                  className="w-full"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "…" : t.login.submit}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setStep("forgot");
                    setError(null);
                    setInfo(null);
                  }}
                  className="text-xs text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
                >
                  {t.login.forgot}
                </button>
              </div>
            </form>
          ) : step === "forgot" ? (
            <form onSubmit={(e) => void submitForgot(e)} className="space-y-4">
              <div>
                <h2 className="text-sm font-semibold">{t.login.forgotTitle}</h2>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                  {t.login.forgotText}
                </p>
              </div>
              <Input
                type="email"
                name="email"
                className="w-full"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                autoFocus
              />
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              {info && !error && (
                <p className="text-sm text-emerald-600 dark:text-emerald-400">{info}</p>
              )}
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "…" : t.login.forgotSubmit}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setStep("credentials");
                    setError(null);
                    setInfo(null);
                  }}
                  className="text-xs text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
                >
                  {t.twofa.back}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={(e) => void submitOtp(e)} className="space-y-4">
              <div>
                <h2 className="text-sm font-semibold">{t.twofa.title}</h2>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{t.twofa.prompt}</p>
              </div>
              <Input
                className="w-full text-center tracking-[0.3em]"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                required
                autoFocus
              />
              <label className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300">
                <input
                  type="checkbox"
                  className="accent-accent"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                />
                {t.twofa.trustDevice}
              </label>
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              {info && !error && (
                <p className="text-sm text-emerald-600 dark:text-emerald-400">{info}</p>
              )}
              <Button type="submit" className="w-full" disabled={busy || code.length < 6}>
                {busy ? "…" : t.twofa.verify}
              </Button>
              <div className="flex justify-between text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setStep("credentials");
                    setCode("");
                    setError(null);
                    setInfo(null);
                  }}
                  className="text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
                >
                  {t.twofa.back}
                </button>
                <button
                  type="button"
                  onClick={() => void sendCode()}
                  className="text-accent hover:underline"
                >
                  {t.twofa.resend}
                </button>
              </div>
            </form>
          )}
        </Card>
      </main>
    </div>
  );
}
